/*
 * Display names for domain values, in the browser's language. The domain classes and
 * ContentUtils build English; this rewords from the underlying CODE, never by
 * matching the English text. Mirrors Android's shared/i18n/DomainLabels.java.
 *
 * Every *Options() returns the same shape as the ContentUtils list it replaces
 * ({ '@rid': code, name: label, ... }), so a v-select / RcDropdownSelect with
 * item-value="@rid" item-title="name" keeps saving the code. Only `name` (and
 * `details`) change with the language.
 */
import { t } from './index.js';
import ContentUtils from '@/utils/ContentUtils.js';
import StringUtils from '@/utils/StringUtils.js';

/**
 * The coded-options pattern: `items` carry the saved code in `valueField`; `labels`
 * maps code -> message key (or a function code -> text). Items without a mapping
 * keep their current name, so a new server value still shows.
 */
export function codedOptions(items, labels, { valueField = '@rid', titleField = 'name' } = {}) {
  return items.map((item) => {
    const code = item[valueField];
    const label = typeof labels === 'function' ? labels(code, item) : labels[code];
    if (label === undefined || label === null) {
      return { ...item };
    }
    const text = typeof labels === 'function' ? label : t(label);
    return { ...item, [titleField]: text };
  });
}

const CATEGORY_KEYS = {
  '#23:1': 'vehicle_pickup_label',
  '#23:5': 'vehicle_trailer_label',
  '#23:7': 'domain_category_bucket_truck',
  '#23:6': 'vehicle_bus_label',
  '#23:24': 'domain_category_motor_coach',
  '#23:23': 'vehicle_school_bus_label',
  '#23:0': 'vehicle_car_label',
  '#23:12': 'domain_category_cube_van',
  '#23:8': 'domain_category_drill_truck',
  '#23:3': 'domain_category_dump_truck',
  '#23:13': 'domain_category_highway_tractor',
  '#23:15': 'domain_category_limo',
  '#23:11': 'domain_category_straight_truck',
  '#23:19': 'vehicle_school_purpose_vehicle_label',
  '#23:2': 'domain_category_suv',
  '#23:4': 'domain_category_tow_truck',
  '#23:9': 'domain_category_vacuum_truck',
  '#23:10': 'vehicle_van_label',
  '#23:14': 'domain_unknown',
};

/** ContentUtils.BRAKE_TYPES short labels (the select). */
const BRAKE_KEYS = {
  abs: 'vehicle_air_brake_label',
  ebs: 'vehicle_electric_brake_label',
  hbs: 'vehicle_hydraulic_brake_label',
  nbs: 'vehicle_none_brake_label',
};

/** Vehicle.brakeTypeDisplay long labels (details). */
const BRAKE_DISPLAY_KEYS = {
  abs: 'domain_brakes_air',
  ebs: 'domain_brakes_electric',
  hbs: 'domain_brakes_hydraulic',
};

const UNIT_KEYS = { KM: 'veh_kilometers', MI: 'veh_miles' };

const COUNTRY_KEYS = { Canada: 'domain_country_canada', 'United States': 'domain_country_us' };

/** ContentUtils.ROLES '@rid' and ROLES2 keys. */
const ROLE_KEYS = {
  '#31:0': 'domain_role_driver',
  '#31:7': 'domain_role_nc_driver',
  '#31:3': 'domain_role_manager',
  '#31:4': 'domain_role_owner',
  '#31:2': 'labelMechanic',
  driver: 'domain_role_driver',
  nc_driver: 'domain_role_nc_driver',
  manager: 'domain_role_manager',
  owner: 'domain_role_owner',
  mechanic: 'labelMechanic',
};

const ROLE_DETAIL_KEYS = {
  '#31:0': 'domain_role_driver_details',
  '#31:7': 'domain_role_nc_driver_details',
  '#31:3': 'domain_role_manager_details',
  '#31:4': 'domain_role_owner_details',
  '#31:2': 'domain_role_mechanic_details',
};

const STATE_KEYS = {
  locked: 'domain_state_locked',
  removed: 'domain_state_deleted',
  inactive: 'domain_state_inactive',
  active: 'domain_state_active',
  all: 'domain_state_all',
};

const ITEM_TYPE_KEYS = {
  text: ['domain_item_type_text', 'domain_item_type_text_details'],
  number: ['domain_item_type_number', 'domain_item_type_number_details'],
  list: ['domain_item_type_list', 'domain_item_type_list_details'],
  bool: ['domain_item_type_bool', 'domain_item_type_bool_details'],
};

const OPTION_CATEGORY_KEYS = {
  none: ['labelGood', 'domain_option_good_details'],
  minor: ['dailylog_defect_minor', 'domain_option_minor_details'],
  major: ['dailylog_defect_major', 'domain_option_major_details'],
};

const FREQUENCY_KEYS = {
  monthly: 'domain_freq_monthly',
  quarterly: 'domain_freq_quarterly',
  'semi-annually': 'domain_freq_semi_annually',
  annually: 'domain_freq_annually',
};

const PAYMENT_METHOD_KEYS = {
  creditcard: 'domain_pay_credit_card',
  wiretransfer: 'domain_pay_wire_transfer',
  etransfer: 'domain_pay_etransfer',
  cheque: 'domain_pay_cheque',
};

/** Only the generic sources are translated; partner and brand names stay as they are. */
const SOCIAL_KEYS = {
  WebSearch: 'domain_social_web_search',
  WebAd: 'domain_social_web_ad',
  Friend: 'domain_social_friend',
  Other: 'domain_social_other',
  LandscapeOntario: 'domain_social_landscape_ontario',
};

function lookup(map, code, fallback = '') {
  if (code === null || code === undefined || code === '') {
    return fallback;
  }
  const key = map[code] ?? map[String(code).toLowerCase()];
  return key ? t(key) : String(code);
}

function withDetails(items, keys) {
  return items.map((item) => {
    const pair = keys[item['@rid']];
    if (!pair) {
      return { ...item };
    }
    return { ...item, name: t(pair[0]), details: t(pair[1]) };
  });
}

export default class DomainLabels {

  // ---- Inspections

  /** Inspection.tripDisplay() from the codes. */
  static tripType(isPreTrip, isEquipment = false) {
    if (isEquipment) {
      return t('inspection_general');
    }
    return t(isPreTrip ? 'inspection_pre_trip' : 'inspection_post_trip');
  }

  /** Defect type code (none/minor/major, any case). Empty stays empty. */
  static defectType(code) {
    if (StringUtils.isEmpty(code)) {
      return '';
    }
    if (StringUtils.eic(code, 'none') || StringUtils.eic(code, 'good')) {
      return t('dailylog_defect_none');
    }
    if (StringUtils.eic(code, 'minor')) {
      return t('dailylog_defect_minor');
    }
    if (StringUtils.eic(code, 'major')) {
      return t('dailylog_defect_major');
    }
    return String(code);
  }

  // ---- Features

  /** Feature.nameDisplay() in the current language; other features as nameDisplay() gives them. */
  static featureName(feature) {
    if (feature.isVehicles()) {
      return t('labelVehicles');
    }
    if (feature.isTimesheets()) {
      return t('hos_title');
    }
    if (feature.isEquipment()) {
      return t('vehicle_equipment_label');
    }
    if (feature.isInspections()) {
      return t('app_settings_inspections');
    }
    return feature.nameDisplay();
  }

  // ---- Vehicles

  /** Vehicle category id ('#23:1'...) -> label; replaces categoryDisplayName(). */
  static vehicleCategory(id) {
    return lookup(CATEGORY_KEYS, id);
  }

  static vehicleCategoryOptions() {
    return codedOptions(ContentUtils.CATEGORIES(), CATEGORY_KEYS);
  }

  /** Short brake label (Air / Electric / Hydraulic / None) for a brake code. */
  static brakeType(code) {
    return lookup(BRAKE_KEYS, code);
  }

  /** Vehicle.brakeTypeDisplay() in the current language ("Air Brakes", ... "None"). */
  static brakeTypeDisplay(code) {
    const key = BRAKE_DISPLAY_KEYS[code];
    return t(key || 'vehicle_none_brake_label');
  }

  static brakeTypeOptions() {
    return codedOptions(ContentUtils.BRAKE_TYPES(), BRAKE_KEYS);
  }

  /** Odometer unit code (KM / MI). */
  static odometerUnit(code) {
    return lookup(UNIT_KEYS, code);
  }

  static odometerUnitOptions() {
    return codedOptions(ContentUtils.UNITS(), UNIT_KEYS);
  }

  // ---- People

  static country(code) {
    return lookup(COUNTRY_KEYS, code);
  }

  static countryOptions() {
    return codedOptions(ContentUtils.COUNTRYS(), COUNTRY_KEYS);
  }

  /** Role id ('#31:0') or role key ('driver', 'manager'...). */
  static role(code) {
    return lookup(ROLE_KEYS, code);
  }

  static roleOptions() {
    return ContentUtils.ROLES().map((item) => ({
      ...item,
      name: t(ROLE_KEYS[item['@rid']]),
      details: t(ROLE_DETAIL_KEYS[item['@rid']]),
    }));
  }

  /** Record state code (active / inactive / locked / removed / all). */
  static state(code) {
    return lookup(STATE_KEYS, code);
  }

  static stateOptions() {
    return codedOptions(ContentUtils.STATES(), STATE_KEYS);
  }

  static statusOptions() {
    return codedOptions(ContentUtils.STATUS(), STATE_KEYS);
  }

  // ---- Checklists

  static checklistItemType(code) {
    const pair = ITEM_TYPE_KEYS[code];
    return pair ? t(pair[0]) : (code ?? '');
  }

  static checklistItemTypeOptions() {
    return withDetails(ContentUtils.ChecklistItemTypes(), ITEM_TYPE_KEYS);
  }

  static checklistOptionCategory(code) {
    const pair = OPTION_CATEGORY_KEYS[code];
    return pair ? t(pair[0]) : (code ?? '');
  }

  static checklistOptionCategoryOptions() {
    return withDetails(ContentUtils.ChecklistItemOptionCategories(), OPTION_CATEGORY_KEYS);
  }

  // ---- Billing and sign-up

  static paymentFrequency(code) {
    return lookup(FREQUENCY_KEYS, code);
  }

  static paymentFrequencyOptions() {
    return codedOptions(ContentUtils.PAYMENT_FREQUENCYS(), FREQUENCY_KEYS);
  }

  static paymentMethod(code) {
    return lookup(PAYMENT_METHOD_KEYS, code);
  }

  static paymentMethodOptions() {
    return codedOptions(ContentUtils.PAYMENT_METHODS(), PAYMENT_METHOD_KEYS);
  }

  /** "  1 to 5" -> "1 à 5"; the code ('1_to_5') is what is saved. */
  static fleetSizeOptions() {
    return ContentUtils.FLEET_SIZE().map((item) => {
      const match = /^(\d+)_to_(\d+)$/.exec(item['@rid']);
      if (!match) {
        return { ...item };
      }
      // Show the label's own numbers (the 75_to_100 code is labelled 76 to 100).
      const nums = item.name.trim().split(/\s+to\s+/);
      return { ...item, name: t('domain_fleet_range', nums[0], nums[1]) };
    });
  }

  static socialOptions() {
    return codedOptions(ContentUtils.SOCIALS(), SOCIAL_KEYS);
  }

  static adOptions() {
    return codedOptions(ContentUtils.ADS(), SOCIAL_KEYS);
  }

  // ---- Paging

  static pageSizeOptions() {
    return ContentUtils.PAGE_SIZES().map((item) => ({ ...item, name: t('domain_page_size', item['@rid']) }));
  }

  static pageIndexOptions(totalItems, pageSize) {
    return ContentUtils.PAGE_INDEXES(totalItems, pageSize)
      .map((item) => ({ ...item, name: t('domain_page_number', String(item['@rid'])) }));
  }
}
